import { FC, useState } from 'react';
import { Avatar, Button, Divider, Typography } from 'antd';
import { TbArrowUpRight, TbCash, TbChartLine, TbClock, TbCopy, TbGift, TbLink, TbSend } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReferralSummary } from '@/hooks/useReferralSummary';
import type { ReferralAnalyticsInterval } from '@/services/database';
import {
    AnalyticsPeriodDropdown,
    AnalyticsSectionCard,
    copyReferralLinkToClipboard,
    EnrichedAnchorTag,
    EXTERNAL_LINKS,
    FinancialOverviewMetricCardGrid,
    FullHeightFlexColumn,
    LearnMoreFooter,
    OverflowFadeScrollArea,
    PlatformAlert,
    ReferralInvitationCard,
    ReferralLinkCard,
    resolveReferralSparkline,
    SettingsCalloutCard,
} from '@/shared';
import { ButtonSkeleton } from '@/shared/components/skeletons';
import { corporateLogo, formatStripeMoney } from '@/utilities';

import ReferralActivityDashboard from './ReferralActivityDashboard';

const REFERRAL_ANALYTICS_OPTIONS = [
    { label: '1 Week', value: '7d' },
    { label: '1 Month', value: '30d' },
    { label: '1 Quarter', value: '90d' },
    { label: '1 Year', value: '1y' },
    { dividerBefore: true, label: 'All Time', value: 'all' },
] satisfies Array<{ dividerBefore?: boolean; label: string; value: ReferralAnalyticsInterval }>;

const REFERRAL_INVITATIONS_SECTION_ID = 'referral-invitations-section';
const REFERRAL_PERFORMANCE_SECTION_ID = 'referral-performance-section';

const Referrals: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [analyticsInterval, setAnalyticsInterval] = useState<ReferralAnalyticsInterval>('30d');
    const { data: summary, isLoading } = useReferralSummary(analyticsInterval);
    const analyticsIntervalLabel =
        REFERRAL_ANALYTICS_OPTIONS.find(({ value }) => value === analyticsInterval)?.label ?? analyticsInterval;
    const currency = summary?.partner.stats.currency ?? 'usd';
    const referralUrl = summary?.partner.referralUrl ?? '';
    const commissionDurationMonths = summary?.program.terms.commissionDurationMonths ?? 12;
    const commissionPercent = summary?.program.terms.commissionPercent ?? 25;
    const holdingPeriodDays = summary?.program.terms.holdingPeriodDays ?? 30;
    const dubLogo = corporateLogo(EXTERNAL_LINKS.dub.home);
    const metricValue = (value: number | null | undefined, formatter: (metric: number) => string) => {
        if (isLoading) {
            return <ButtonSkeleton active size='small' style={{ width: '5rem' }} />;
        }
        return formatter(value ?? 0);
    };
    const formatEarnings = (value: number) =>
        value === 0
            ? new Intl.NumberFormat('en-US', {
                  currency: currency.toUpperCase(),
                  maximumFractionDigits: 0,
                  style: 'currency',
              }).format(0)
            : formatStripeMoney(value, currency);
    const metrics = [
        {
            description: 'Commission earnings recorded for your eligible referral activity.',
            key: 'earnings',
            label: 'Earnings',
            sparkline: resolveReferralSparkline({
                analytics: summary?.analytics,
                points: summary?.partner.trends.earnings,
                value: summary?.partner.stats.earnings,
            }),
            sparklineLabel: `Referral earnings for ${analyticsIntervalLabel.toLowerCase()}`,
            sparklineValueFormatter: formatEarnings,
            value: metricValue(summary?.partner.stats.earnings, formatEarnings),
        },
        {
            description: 'Visits to your personal referral link tracked by Dub.',
            key: 'clicks',
            label: 'Clicks',
            sparkline: resolveReferralSparkline({
                analytics: summary?.analytics,
                points: summary?.partner.trends.clicks,
                value: summary?.partner.stats.clicks,
            }),
            sparklineLabel: `Referral link clicks for ${analyticsIntervalLabel.toLowerCase()}`,
            sparklineValueFormatter: (value: number) => value.toLocaleString(),
            value: metricValue(summary?.partner.stats.clicks, (value) => value.toLocaleString()),
        },
        {
            description: 'New Countercyclical accounts attributed to your referral link.',
            key: 'leads',
            label: 'Leads',
            sparkline: resolveReferralSparkline({
                analytics: summary?.analytics,
                points: summary?.partner.trends.leads,
                value: summary?.partner.stats.leads,
            }),
            sparklineLabel: `Referral leads for ${analyticsIntervalLabel.toLowerCase()}`,
            sparklineValueFormatter: (value: number) => value.toLocaleString(),
            value: metricValue(summary?.partner.stats.leads, (value) => value.toLocaleString()),
        },
        {
            description: 'Eligible paid invoices generated by referred accounts.',
            key: 'sales',
            label: 'Sales',
            sparkline: resolveReferralSparkline({
                analytics: summary?.analytics,
                points: summary?.partner.trends.sales,
                value: summary?.partner.stats.sales,
            }),
            sparklineLabel: `Referral sales for ${analyticsIntervalLabel.toLowerCase()}`,
            sparklineValueFormatter: (value: number) => value.toLocaleString(),
            value: metricValue(summary?.partner.stats.sales, (value) => value.toLocaleString()),
        },
    ];

    const programDescription = (
        <>
            We use <EnrichedAnchorTag label='Dub' url={EXTERNAL_LINKS.dub.home} /> for managing & running referrals.{' '}
            Earn {commissionPercent}% of each eligible monthly or annual subscription payment for up to{' '}
            {commissionDurationMonths} months after a referred customer&apos;s first paid transaction. Signups and free
            accounts do not generate commission.
        </>
    );
    const scrollToSection = (sectionId: string) => {
        document.querySelector(`#${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const programSteps = [
        {
            action: (
                <Button
                    disabled={!referralUrl}
                    icon={<TbCopy />}
                    onClick={() => void copyReferralLinkToClipboard(referralUrl)}
                    size='small'
                >
                    Copy Referral Link
                </Button>
            ),
            icon: <TbLink />,
            label: 'Share your personal link',
        },
        {
            action: (
                <Button icon={<TbSend />} onClick={() => scrollToSection(REFERRAL_INVITATIONS_SECTION_ID)} size='small'>
                    Send Invitations
                </Button>
            ),
            icon: <TbCash />,
            label: 'A referred customer purchases an eligible plan',
        },
        {
            action: (
                <Button
                    icon={<TbChartLine />}
                    onClick={() => scrollToSection(REFERRAL_PERFORMANCE_SECTION_ID)}
                    size='small'
                >
                    View Performance
                </Button>
            ),
            icon: <TbGift />,
            label: 'Eligible paid invoices generate commission automatically',
        },
    ];

    return (
        <FullHeightFlexColumn
            style={{ margin: '0 auto', maxHeight: '100%', maxWidth: '62rem', overflow: 'hidden', width: '100%' }}
        >
            <header style={{ boxSizing: 'border-box', flex: 'none' }}>
                <Typography.Title id='referrals-heading' level={1} style={{ marginBottom: '0.25rem' }}>
                    Refer & Earn
                </Typography.Title>
                <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                    Refer prospective customers to Countercyclical, track paid conversions, and review your commissions.
                </Typography.Paragraph>
                <Divider style={{ margin: '1rem 0 0' }} />
            </header>
            <OverflowFadeScrollArea
                aria-label='Referral program sections'
                containerStyle={{ flex: '1 1 0', minHeight: 0 }}
                data-referrals-scroll-region='true'
            >
                <section
                    aria-labelledby='referrals-heading'
                    style={{
                        boxSizing: 'border-box',
                        padding: '1rem 0 0.75rem',
                        width: '100%',
                    }}
                >
                    <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
                        <SettingsCalloutCard
                            description={programDescription}
                            icon={<TbGift />}
                            title='How the Referral Program Works'
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gap: '0.75rem',
                                }}
                            >
                                {programSteps.map(({ action, icon, label }, index) => (
                                    <div
                                        key={label}
                                        style={{
                                            alignItems: 'center',
                                            display: 'grid',
                                            gap: '1rem',
                                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                                        }}
                                    >
                                        <div
                                            style={{
                                                alignItems: 'center',
                                                display: 'flex',
                                                gap: '0.5rem',
                                                minWidth: 0,
                                            }}
                                        >
                                            <span
                                                aria-hidden='true'
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    display: 'inline-flex',
                                                    flex: 'none',
                                                    fontSize: '1rem',
                                                }}
                                            >
                                                {icon}
                                            </span>
                                            <Typography.Text type='secondary'>
                                                {index + 1}. {label}
                                            </Typography.Text>
                                        </div>
                                        <div style={{ flex: 'none' }}>{action}</div>
                                    </div>
                                ))}
                                <PlatformAlert
                                    description={`Commissions from both monthly and annual plans remain pending for ${holdingPeriodDays} days before becoming eligible for payout. Annual plans become non-refundable after this same ${holdingPeriodDays}-day period. Learn more in our privacy policy, terms of service, and referral program policy.`}
                                    icon={<TbClock />}
                                    showIcon
                                    status='info'
                                    title={`${holdingPeriodDays}-Day Holding Period`}
                                />
                                <Divider style={{ margin: '0.25rem 0 0' }} />
                                <div
                                    aria-label='Dub referral management'
                                    style={{
                                        display: 'grid',
                                        gap: '0.5rem',
                                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                    }}
                                >
                                    <Button
                                        href={EXTERNAL_LINKS.dub.partnerPrograms}
                                        icon={
                                            <Avatar
                                                alt='Dub'
                                                shape='square'
                                                size={16}
                                                src={dubLogo}
                                                style={{ borderRadius: token.borderRadiusSM }}
                                            />
                                        }
                                        rel='noopener noreferrer'
                                        size='small'
                                        style={{ width: '100%' }}
                                        target='_blank'
                                    >
                                        Manage Referrals in Dub
                                        <TbArrowUpRight
                                            aria-hidden='true'
                                            style={{ color: token.colorTextTertiary, marginInlineStart: '0.25rem' }}
                                        />
                                    </Button>
                                    <Button
                                        href={EXTERNAL_LINKS.dub.payouts}
                                        icon={
                                            <Avatar
                                                alt='Dub'
                                                shape='square'
                                                size={16}
                                                src={dubLogo}
                                                style={{ borderRadius: token.borderRadiusSM }}
                                            />
                                        }
                                        rel='noopener noreferrer'
                                        size='small'
                                        style={{ width: '100%' }}
                                        target='_blank'
                                    >
                                        Manage Dub Payouts
                                        <TbArrowUpRight
                                            aria-hidden='true'
                                            style={{ color: token.colorTextTertiary, marginInlineStart: '0.25rem' }}
                                        />
                                    </Button>
                                </div>
                            </div>
                        </SettingsCalloutCard>

                        <ReferralLinkCard />

                        <ReferralInvitationCard id={REFERRAL_INVITATIONS_SECTION_ID} />

                        <AnalyticsSectionCard
                            ariaLabel='Referral performance'
                            contentStyle={{ padding: '0.75rem' }}
                            description={`Live referral performance for ${analyticsIntervalLabel.toLowerCase()}.`}
                            extra={
                                <AnalyticsPeriodDropdown
                                    onChange={setAnalyticsInterval}
                                    options={REFERRAL_ANALYTICS_OPTIONS}
                                    value={analyticsInterval}
                                />
                            }
                            icon={<TbChartLine />}
                            id={REFERRAL_PERFORMANCE_SECTION_ID}
                            title='Performance'
                        >
                            <FinancialOverviewMetricCardGrid columnCount={2} items={metrics} showDescriptionTooltips />
                        </AnalyticsSectionCard>

                        <ReferralActivityDashboard />
                    </div>
                </section>
            </OverflowFadeScrollArea>
            <footer
                aria-label='Referral program documentation'
                style={{ flex: 'none', paddingBottom: '0.5rem', textAlign: 'center', width: '100%' }}
            >
                <Divider style={{ margin: '0 0 0.5rem' }} />
                <LearnMoreFooter align='center' topic='the Countercyclical referral program' />
            </footer>
        </FullHeightFlexColumn>
    );
};

export default Referrals;
